import React, { useEffect, useState } from 'react';
import { styled } from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';

import CompassHeader, { PageType } from 'Compass/components/CompassHeader';
import { SelectionProvider, useSelectionContext } from 'Compass/profile-lab/contexts/SelectionContext';
import ErrorBoundary from 'Compass/profile-lab/components/ErrorBoundary';
import { ProcessingStatusBar, ProcessingTimer } from 'Compass/profile-lab/styles/chat';

const ProfileLabContent = () => {
  const { growCount, selectedCount, totalCount, isProcessing, processingStartTime, processingStatus } = useSelectionContext();
  const location = useLocation();
  const [elapsedTime, setElapsedTime] = useState(0);

  const [prevIsProcessing, setPrevIsProcessing] = useState(isProcessing);
  if (isProcessing !== prevIsProcessing) {
    setPrevIsProcessing(isProcessing);
    if (!isProcessing) {
      setElapsedTime(0);
    }
  }

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  useEffect(() => {
    if (!isProcessing || !processingStartTime) {
      return;
    }

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - processingStartTime) / 1000);
      setElapsedTime(elapsed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isProcessing, processingStartTime]);

  const formatElapsedTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <ErrorBoundary>
      <ContentWrapper>
        <Toolbar>
          <NavLink to="/profile-lab/subject" $active={isActive('/profile-lab/subject')}>Subject</NavLink>
          <NavLink to="/profile-lab/sample" $active={isActive('/profile-lab/sample')}>Sample</NavLink>
          <NavLink to="/profile-lab/people" $active={isActive('/profile-lab/people')}>People</NavLink>
          <NavLink to="/profile-lab/calendar" $active={isActive('/profile-lab/calendar')}>Calendar</NavLink>
          <NavLink to="/profile-lab/map" $active={isActive('/profile-lab/map')}>Map</NavLink>
          <NavLink to="/profile-lab/selection" $active={isActive('/profile-lab/selection')}>Selection</NavLink>
          <NavLink to="/profile-lab/stage" $active={isActive('/profile-lab/stage')}>Stage</NavLink>
          <NavLink to="/profile-lab/chat" $active={isActive('/profile-lab/chat')}>Chat</NavLink>
          <SelectionCounter>
            <GrowCount>{growCount}</GrowCount> /{' '}
            <SelectedCount>{selectedCount}</SelectedCount> / {totalCount}
          </SelectionCounter>
        </Toolbar>
        <ProcessingStatusBar $show={isProcessing}>
          <ProcessingTimer>{formatElapsedTime(elapsedTime)}</ProcessingTimer> Processing{processingStatus && ` - ${processingStatus}`}
        </ProcessingStatusBar>
        <GridContainer>
          <Outlet />
        </GridContainer>
      </ContentWrapper>
    </ErrorBoundary>
  );
};

const ProfileLabPage = () => {
  return (
    <>
      <CompassHeader pageType={PageType.ProfileLab}/>
      <PageContainer>
        <SelectionProvider>
          <ProfileLabContent />
        </SelectionProvider>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  width: 100%;
  height: calc(100vh - 64px);
  background: #f5f7fa;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Toolbar = styled.div`
  position: sticky;
  top: 0;
  background-color: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 10px 20px;
  z-index: 100;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const NavLink = styled(Link)<{ $active: boolean }>`
  color: ${props => props.$active ? '#ffffff' : '#2d3748'};
  text-decoration: none;
  font-size: 16px;
  padding: 5px 10px;
  border-radius: 4px;
  transition: background-color 0.2s;
  background-color: ${props => props.$active ? '#4299e1' : 'transparent'};
  font-weight: ${props => props.$active ? 600 : 400};

  &:hover {
    background-color: ${props => props.$active ? '#4299e1' : '#e2e8f0'};
  }
`;

const SelectionCounter = styled.span`
  color: #2d3748;
  font-size: 16px;
  padding: 5px 10px;
  margin-left: auto;
`;

const GrowCount = styled.span`
  color: #10b981;
  font-weight: 600;
`;

const SelectedCount = styled.span`
  color: #9333ea;
  font-weight: 600;
`;

const GridContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 10px;
  flex: 1;
  overflow: auto;
  align-content: flex-start;
`;

export default ProfileLabPage;
